import React, { useState, useEffect, useRef } from "react";
import "./TestimonialsSection.css";

const FALLBACK_TESTIMONIALS = [
  {
    name: "Michael R.",
    text: "Polania Construction transformed our backyard into an outdoor oasis. The attention to detail in the paver work is incredible—every stone perfectly placed. The team was professional, punctual, and the quality speaks for itself.",
    rating: 5,
    relativeTime: "a year ago",
  },
  {
    name: "Sandra K.",
    text: "We hired Polania for our front steps and walkway. The craftsmanship is outstanding—you can tell these are true artisans. They took the time to understand exactly what we wanted and delivered beyond our expectations. Highly recommend!",
    rating: 5,
    relativeTime: "2 years ago",
  },
  {
    name: "David & Lisa M.",
    text: "Our property had serious drainage issues and we needed a retaining wall solution. Polania not only solved the problem but created a beautiful feature that adds real value to our home. Professional from start to finish.",
    rating: 5,
    relativeTime: "a year ago",
  },
  {
    name: "Robert T.",
    text: "After getting quotes from several contractors, we chose Polania based on their reputation and expertise. Best decision we made. The concrete work is flawless and they completed the job on time and on budget. True professionals.",
    rating: 5,
    relativeTime: "3 years ago",
  },
];

// Try to load build-time fetched reviews
let buildTimeReviews = null;
try {
  buildTimeReviews = require("../data/reviews.json");
} catch {
  // reviews.json doesn't exist yet — will use fallback
}

export default function TestimonialsSection() {
  const hasGoogleData = buildTimeReviews && buildTimeReviews.reviews?.length > 0;
  const [testimonials] = useState(
    hasGoogleData ? buildTimeReviews.reviews : FALLBACK_TESTIMONIALS
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 15000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const renderStars = (rating) => {
    return Array(rating)
      .fill(0)
      .map((_, i) => (
        <svg
          key={i}
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="star"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ));
  };

  const ratingText = hasGoogleData
    ? `${buildTimeReviews.overallRating} Rating on Google (${buildTimeReviews.totalReviews} reviews)`
    : "5.0 Rating on Google";

  return (
    <section className="testimonials-section" ref={sectionRef}>
      <div className={`testimonials-container ${isVisible ? "visible" : ""}`}>
        {/* Header */}
        <div className="testimonials-header">
          <span className="section-label">TESTIMONIALS</span>
          <h2 className="section-title">
            WHAT OUR <span className="gold-text">CLIENTS</span> SAY
          </h2>
        </div>

        {/* Testimonial Cards */}
        <div className="testimonials-carousel">
          <div className="quote-icon">
            <svg width="60" height="60" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
            </svg>
          </div>

          <div className="testimonials-track">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`testimonial-card ${index === activeIndex ? "active" : ""}`}
              >
                <div className="testimonial-rating">
                  {renderStars(testimonial.rating)}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  {testimonial.profilePhoto ? (
                    <img
                      src={testimonial.profilePhoto}
                      alt={testimonial.name}
                      className="author-avatar-img"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="author-avatar"
                    style={testimonial.profilePhoto ? { display: "none" } : {}}
                  >
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">{testimonial.name}</h4>
                    {testimonial.relativeTime && (
                      <p className="author-location">{testimonial.relativeTime}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Dots */}
          <div className="testimonials-nav">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`nav-dot ${index === activeIndex ? "active" : ""}`}
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>

          {/* Progress Bar */}
          <div className="testimonial-progress">
            <div
              className="progress-bar"
              style={{
                width: `${((activeIndex + 1) / testimonials.length) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Google Reviews CTA */}
        <div className="reviews-cta">
          <div className="google-rating">
            <div className="google-stars">{renderStars(5)}</div>
            <span className="google-text">{ratingText}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
